import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { CashTransactionRow } from '@/features/finance/cash-ledger.types';

export interface CandidateListProps {
  readonly candidates: readonly CashTransactionRow[];
  readonly selectedTxnId: string | null;
  readonly onSelect: (txnId: string) => void;
}

export function CandidateList({ candidates, selectedTxnId, onSelect }: CandidateListProps) {
  if (candidates.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        No unmatched cash transactions in this currency and direction.
      </p>
    );
  }
  return (
    <ul
      className="max-h-72 divide-y overflow-y-auto rounded-md border"
      role="listbox"
      aria-label="Unmatched cash transactions"
    >
      {candidates.map((txn) => (
        <CandidateRow
          key={txn.id}
          txn={txn}
          selected={txn.id === selectedTxnId}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
}

function CandidateRow({
  txn,
  selected,
  onSelect,
}: Readonly<{
  txn: CashTransactionRow;
  selected: boolean;
  onSelect: (txnId: string) => void;
}>) {
  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={selected}
        onClick={() => {
          onSelect(txn.id);
        }}
        data-testid={`link-cash-event-candidate-${txn.id}`}
        className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-accent ${selected ? 'bg-accent' : ''}`}
      >
        <div className="flex flex-col">
          <span className="font-medium">{txn.date ?? '—'}</span>
          <span className="text-xs text-muted-foreground">
            {txn.counterparty ?? 'Unknown counterparty'}
            {txn.reference !== null && txn.reference !== '' ? ` · ${txn.reference}` : ''}
          </span>
        </div>
        <span className="font-mono tabular-nums">
          {txn.net} {txn.currency ?? ''}
        </span>
      </button>
    </li>
  );
}

export interface AmountFieldProps {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly currency: string;
}

export function AmountField({ value, onChange, currency }: AmountFieldProps) {
  return (
    <div className="space-y-1">
      <Label htmlFor="link-cash-event-amount">Amount to apply ({currency})</Label>
      <Input
        id="link-cash-event-amount"
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </div>
  );
}

export interface SubmitFooterProps {
  readonly invoiceId: string;
  readonly selectedTxnId: string | null;
  readonly amount: string;
  readonly isPending: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}

export function SubmitFooter({
  invoiceId,
  selectedTxnId,
  amount,
  isPending,
  onCancel,
  onSubmit,
}: SubmitFooterProps) {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        type="button"
        disabled={selectedTxnId === null || isPending || amount === ''}
        onClick={onSubmit}
        data-testid={`invoice-${invoiceId}-link-cash-event-submit`}
      >
        {isPending ? 'Linking…' : 'Link'}
      </Button>
    </DialogFooter>
  );
}
